import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Input, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import Select from 'react-select';
import _ from 'lodash';
import moment from "moment";
import { SingleDatePicker } from '.';
import { api, helpers, UserContext } from '../utils';
import { toast } from "react-toastify";

export default function CartAddModal(props) {
   const userCtx = useContext(UserContext);
   const { currentUser } = userCtx;
   const [baseUnit, setBase] = useState(1);
   const [item, setItem] = useState(props.item);
   const [minQty, setMin] = useState(1);
   const minDate = (new Date()).setDate((new Date()).getDate() + 0)
   const [orderSites, setOrderSites] = useState([]);
   let modalClassName = userCtx.theme ? `${userCtx.theme}` : 'lightTheme';

   useEffect(() => {
      if (item) {
         const itemCopy = _.assign({}, item)
         let base = props.item.baseUnit || 1;
         let moq = props.item.realMinOrderQty || props.item.minOrderQty || 1;
         if (base > 1) setBase(base);
         if (moq > 1) setMin(moq);

         if (moq > 1) {
            // A MOQ amount exists on a lot-tracked item BOM
            itemCopy.quantity = moq;
            itemCopy.realMinOrderQty = moq; // in case no realMinOrderQty was provided
         } else {
            // Everything else defaults to 1
            itemCopy.quantity = 1;
            itemCopy.realMinOrderQty = 1;
         }
         itemCopy.requiredDate = moment(minDate).toDate();
         itemCopy.orderSite = props.item.possibleOrderSites[0].siteId;
         setOrderSites(_.map(item.possibleOrderSites, site => { return { value: site.siteId, label: site.siteWithDescription } }))
         setItem(itemCopy)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   function addItemToCart() {
      if (currentUser
         && (currentUser.isAPUCS || currentUser.isSalesperson || currentUser.isAnyCustomerTypeRole)
         && helpers.validateLineItemQty(item.quantity, minQty, baseUnit)
      ) {
         let payload = {
            ItemNumber: item.itemNumber,
            ItemDescription: item.itemWithDesc,
            Quantity: item.quantity,
            OrderSite: item.orderSite,
            RequiredDate: item.requiredDate,
            Notes: item.orderNotes,
            ShoppingCartId: userCtx?.shoppingCart?.shoppingCartId || 0,
            CustomerId: userCtx?.currentUser?.isAnyCustomerTypeRole
               ? helpers.activeCustomerId(userCtx)
               : userCtx?.customerFilter?.value,
         }

         if (_.isNil(payload.CustomerId)) {
            toast.error("Customer Id is null, reselect your customer in the Customer Dashboard...");
            return;
         }
         if (payload.ShoppingCartId === 0) {
            toast.error("Shopping Cart Id is null, reselect your customer in the Customer Dashboard and re add your items...");
            return;
         }

         return api
            .post("Cart/AddItemToCart", payload)
            .then((response) => {
               if (response.data.success) {
                  const saved = response.data.cartItem;
                  const cartCopy = _.assign({ cartItems: [] }, userCtx.shoppingCart);
                  const foundIndex = _.findIndex(cartCopy.cartItems, line => line.itemNumber === saved.itemNumber);
                  if (foundIndex === -1) {
                     cartCopy.cartItems.push(saved);
                  } else {
                     cartCopy.cartItems[foundIndex] = saved;
                  }
                  userCtx.setShoppingCart(cartCopy);
               } else {
                  toast.error(response.data.message);
               }
            })
            .catch(api.catchHandler)
            .finally(props.exitCallback);
      } else {
         if (!currentUser?.isAPUCS && !currentUser?.isSalesperson && !currentUser?.isAnyCustomerTypeRole) {
            toast.error("User does not have permission to add this to the cart.");
         } else {
            helpers.alertMinQty(item.itemNumber, minQty, baseUnit)
         }
      }
   }

   function handleChange(field, value) {
      const itemCopy = _.assign({}, item);
      itemCopy[field] = value;
      setItem(itemCopy);
   }

   function handleOptionEnabled(opt) {
      // Consigned customers can only order from their own site
      return (currentUser.isConsignedCustomerRole && opt !== currentUser.customerId);
   }

   return (
      <Modal size="lg" isOpen={true} contentClassName={modalClassName} >
         <ModalHeader children="Add To Cart" toggle={props.exitCallback} />
         <ModalBody>
            <Row>
               <Col xs={3}>
                  <h5>Item Number:</h5>
               </Col>
               <Col xs={9}>
                  <div>{userCtx.currentUser && !userCtx.currentUser.isAnyCustomerTypeRole
                     ? item.itemNumber
                     : item.customerItemNumber || item.custItemNumber}</div>
               </Col>
            </Row>
            <Row>
               <Col xs={3}>
                  <h5>Item Description:</h5>
               </Col>
               <Col xs={9}>
                  <div>{userCtx.currentUser && !userCtx.currentUser.isAnyCustomerTypeRole
                     ? item.customerItemDescription || item.itemDescription
                     : item.customerItemDescription || item.custItemDescription}</div>
               </Col>
            </Row>
            <Row className="mb-2">
               <Col xs={3}>
                  <h5>Quantity:</h5>
               </Col>
               <Col xs={9}>
                  <Input
                     type="number"
                     value={item.quantity}
                     onChange={e => { handleChange("quantity", e.target.value) }}
                  />
               </Col>
            </Row>
            <Row className="mb-2">
               <Col xs={3}>
                  <h5>Req Date:</h5>
               </Col>
               <Col xs={9}>
                  <SingleDatePicker
                     minDate={minDate}
                     selected={item.requiredDate}
                     onChangeCallback={e => handleChange("requiredDate", e)}
                  />
               </Col>
            </Row>
            <Row className="mb-2">
               <Col xs={3}>
                  <h5>Order Site:</h5>
               </Col>
               <Col xs={9}>
                  <Select
                     classNamePrefix='serviceThreadSelect'
                     options={_.filter(orderSites, site => { return site.value !== item.orderSite })}
                     value={_.find(orderSites, site => { return site.value === item.orderSite })}
                     setValue={() => _.find(orderSites, site => { return site.value === item.orderSite })}
                     onChange={e => handleChange("orderSite", e.value)}
                     isOptionDisabled={(opt) => handleOptionEnabled(opt.value)}
                  />
               </Col>
            </Row>
            <Row className="mb-2">
               <Col xs={3}>
                  <h5>Notes:</h5>
               </Col>
               <Col xs={9}>
                  <Input
                     value={(item && item.orderNotes) || ""}
                     onChange={e => handleChange("orderNotes", e.target.value)}
                  />
               </Col>
            </Row>
            <Button
               className="float-end shredButton"
               children="Add To Cart"
               onClick={() => addItemToCart()}
               disabled={orderSites.length === 1 && orderSites[0].value === "NONE"}
            />
         </ModalBody>
      </Modal>
   )
}